const axios = require("axios").default;
import store from "../store/store";

/**
 * converts objects into encoded URI string via stringify function
 */
import qs from "qs";
const apiHashGuid = "8a24fa5b-72e7-4a85-849b-09387185651a";

// if an api call returns an error, log the user out, otherwise return result
function resultIntercept(result) {
  return result;
}
function errorIntercept(error) {
  if (error.response.status === 405) {
    const alertMessage = "You've been logged out due to inactivity";
    const alerts = store.state.session.errorAlerts;
    const hasInactiveAlert = alerts.some(alert => alert.message === alertMessage);
    
    // There may be multiple requests per action, so don't give more than one 
    // Inactiviy alert.
    if (!hasInactiveAlert) {
      store.commit(
        "session/addErrorAlert", 
        { message: "You've been logged out due to inactivity" }
      );
    }

    store.dispatch("session/logOut");
  }

  return Promise.reject(error);
}
const intercept = axios.interceptors.response.use(
  resultIntercept,
  errorIntercept
);

const timerApi = axios.create({
  baseURL: "/timer-api",
});
timerApi.interceptors.response.use(resultIntercept, errorIntercept);

const dashboardApi = axios.create({
  baseURL: "/dashboard-api",
});
dashboardApi.interceptors.response.use(resultIntercept, errorIntercept);

// gets all the user's data
export const getData = token =>
  dashboardApi.get(`GetData?DashboardToken=${token}`);

// refresh the user's token
export const refreshToken = token =>
  dashboardApi.get(`RefreshToken?DashboardToken=${token}`);

// returns customer subscription data from API
export const getSubscriptionOptions = () =>
  dashboardApi.get(`GetSubscriptionOptions`);

// logs the user in and gets their data
export const logIn = (email, password) => {
  const rawString = `${new Date().getTime()}|${email}|${apiHashGuid}|${password}`;
  const hashedString = btoa(rawString);
  return dashboardApi.post(`login`, { data: hashedString });
};

// changes the user's password. Payload is the new password
export const changePassword = (payload, token) =>
  dashboardApi.post(`ChangePassword?DashboardToken=${token}`, payload);

// sends a reset password email to user
export const forgotPassword = payload =>
  dashboardApi.post(`ForgotPassword`, { data: payload });

// changes the user's password using token from password reset email
export const resetPasswordWithToken = (payload, token) =>
  dashboardApi.post(`ResetPasswordWithToken?Token=${token}`, {
    data: payload,
  });

// api call used to save all account data
export const saveAccountData = (payload, token) =>
  dashboardApi.post(`ChangeAccountInfo?DashboardToken=${token}`, payload);

// saves a new developer IP
export const addDeveloperIP = (payload, token) =>
  dashboardApi.post(`AddDevloperIp?DashboardToken=${token}`, {
    name: payload.name,
    ipAddress: payload.ipAddress,
  });

// removes a developer IP
export const removeDeveloperIP = (payload, token) =>
  dashboardApi({
    method: "DELETE",
    url: `RemoveDevloperIp?DashboardToken=${token}&IpAddressId=${payload.id}`,
  });

// changes URL hijacking setting
export const changeURLHijacking = (payload, token) =>
  dashboardApi.post(
    `ChangeAllowUrlHijacking?${qs.stringify({
      DashboardToken: token,
      AllowUrlHijacking: payload,
    })}`
  );

// changes user's subscription type
export const changeSubscription = (payload, token) =>
  dashboardApi({
    method: "POST",
    url: `ChangeSubscription?${qs.stringify({
      DashboardToken: token,
    })}`,
    data: {
      subscriptionType: payload.subscriptionType,
      billingCycle: payload.billingCycle,
      ExpMonth: payload.expMonth,
      ExpYear: payload.expYear,
    },
  });

export const closeAccount = token =>
  dashboardApi.post(`CloseAccount?DashboardToken=${token}`);

// updates user's credit card
export const changeCreditCard = (payload, token) =>
  dashboardApi({
    method: "POST",
    url: `ChangeCreditCard?${qs.stringify({
      DashboardToken: token,
    })}`,
    data: {
      subscriptionType: payload.subscriptionType,
      billingCycle: payload.billingCycle,
      cardNumber: payload.cardNumber,
      cvv: payload.cvv,
      postalCode: payload.postalCode,
      expMonth: payload.expMonth,
      expYear: payload.expYear,
    },
  });

// resets api keys
export const resetKeys = token =>
  dashboardApi.post(`ResetSecretKey?DashboardToken=${token}`);

// get timezones
export const getTimezones = function() {
  return timerApi.get(`GetDropdownInfo`);
};

// get timer preview
export const getWidgetPreview = (payload, token) =>
  timerApi({
    method: "POST",
    responseType: "blob",
    url: `GetWidgetPreview?${qs.stringify({
      DashboardToken: token,
      ScaleFactor: payload.scaleFactor,
    })}`,
    data: {
      name: payload.name,
      timerStyle: payload.timerStyle,
      endDateTime: payload.endDateTime,
      endTimeZoneInfo: payload.endTimeZoneInfo,
      backgroundColor: payload.backgroundColor,
      backgroundTransparent: payload.backgroundTransparent,
      textColor: payload.textColor,
      boxColor: payload.boxColor,
      fontFamily: payload.fontFamily,
      fontSize: payload.fontSize,
      languageCode: payload.languageCode,
      shouldUseDynamicLocal: payload.shouldUseDynamicLocal,
      includeDays: payload.includeDays,
      active: payload.active,
      archived: payload.archived,
      discriminator: payload.discriminator,
    },
  });

/**
 * gets widget info, also "sets" the widget the back end will return
 * after making other API calls like updateWidgetDto and updateWidget
 * you must call this first if you want to get the right widget back!
 */
export const getWidgetInfo = (payload, token) => {
  return timerApi.get(
    `GetWidgetInfo?DashboardToken=${token}&WidgetId=${payload.widgetId}`
  );
};

// updates a widget by passing it the entire widget object w/ changes
export const updateWidgetDto = (payload, token) =>
  timerApi.post(`UpdateWidgetDto?DashboardToken=${token}`, payload);

// updates a widget by passing just the "widget" object from the widget / changes
export const updateWidget = (payload, token) =>
  timerApi.post(`UpdateWidget?DashboardToken=${token}`, payload);

// creates a new widget by passing in a widget object
export const createWidget = (payload, token) => {
  return timerApi.post(
    `CreateWidget?${qs.stringify({
      DashboardToken: token,
    })}`,
    {
      name: payload.name,
      timerStyle: parseInt(payload.timerStyle),
      endDateTime: payload.endDateTime,
      endTimeZoneInfo: payload.endTimeZoneInfo,
      backgroundColor: payload.backgroundColor,
      backgroundTransparent: payload.backgroundTransparent,
      textColor: payload.textColor,
      boxColor: payload.boxColor,
      fontFamily: payload.fontFamily,
      fontSize: payload.fontSize,
      languageCode: payload.languageCode,
      shouldUseDynamicLocal: payload.shouldUseDynamicLocal,
      includeDays: payload.includeDays,
      active: payload.active,
      archived: payload.archived,
      discriminator: payload.discriminator,
    }
  );
};

// returns a change summary based on a new billing cycle / subscription type
export const changeSummary = (payload, token) => {
  return dashboardApi.post(
    `ChangeSummary?${qs.stringify({
      DashboardToken: token,
      NewSubscriptionType: payload.subscriptionType,
      NewPlanBillingCycle: payload.billingCycle,
    })}`
  );
};

/**
 * add a new user via an email, first name, last name, and password
 * sent as a base64 encoded string
 */
export const registerUser = ({ email, firstName, lastName, password }) => {
  const rawString = `${new Date().getTime()}|${email}|${firstName}|${lastName}|${apiHashGuid}|${password}`;
  const hashedString = btoa(rawString);
  return dashboardApi.post(`RegisterUser`, { data: hashedString });
};

export default logIn;
